/*
 * MinesSolver — what can be PROVEN about a minesweeper position, and what the odds are
 * where nothing can.
 *
 *   MinesSolver.analyze(view, opts)   exact: every provably safe cell, every provable mine,
 *                                     the probability of a mine in every other cell
 *   MinesSolver.solve(w, h, mine, start, opts)
 *                                     plays a whole board by logic alone from one opening;
 *                                     `solved` means no guess was ever needed
 *   MinesSolver.hint(view, opts)      the simplest next deduction and the digits that prove it
 *   MinesSolver.explainLoss(view, cell)
 *                                     was the fatal cell deducible, was there a safe move,
 *                                     or was it a forced guess — with the odds
 *
 * The solver is given only what a player sees (open cells and their digits);
 * the player's flags are never taken on trust. Certainty is decided by counting
 * which arrangements exist, never by comparing floating-point probabilities to
 * zero: "safe" must mean safe.
 *
 * A classic script without DOM — page, worker and node tests load the same file.
 * Decisions and measurements — docs/why/minesweeper.md.
 */
(function (root) {
    'use strict';

    var Core = root.MinesCore;

    // Binomials: small ones exactly (a group of interchangeable cells never
    // has more than eight), large ones as logarithms (the cells nobody has
    // information about can number in the thousands).
    var SMALL_C = [];
    (function () {
        for (var n = 0; n <= 8; n++) {
            SMALL_C[n] = [];
            for (var k = 0; k <= n; k++) SMALL_C[n][k] = (k === 0 || k === n) ? 1 : SMALL_C[n - 1][k - 1] + SMALL_C[n - 1][k];
        }
    })();
    var LOG_FACT = [0];
    function logFact(n) {
        while (LOG_FACT.length <= n) LOG_FACT.push(LOG_FACT[LOG_FACT.length - 1] + Math.log(LOG_FACT.length));
        return LOG_FACT[n];
    }
    function logChoose(n, k) {
        if (k < 0 || k > n) return -Infinity;
        return logFact(n) - logFact(k) - logFact(n - k);
    }

    var BUDGET_ANALYZE = 400000;
    var BUDGET_SOLVE = 60000;
    // Counters for tests and benchmarks: how often a region was too large to count.
    var counters = { analyses: 0, partial: 0, nodes: 0 };

    // ── The frontier as a system of equations ────────────────────────────────
    // Every open digit with unknown neighbours says "exactly k of these cells
    // are mines". Cells that appear in exactly the same equations are
    // interchangeable, so they are counted as one group with a size.
    function buildSystem(t, isOpen, num, isMine, limitTo) {
        var n = t.n, nb = t.nb;
        var local = new Int32Array(n).fill(-1), cells = [], cons = [], bad = false;
        for (var i = 0; i < n; i++) {
            if (!isOpen[i]) continue;
            if (limitTo && !limitTo[i]) continue;
            var l = nb[i], k = num[i], list = null;
            for (var j = 0; j < l.length; j++) {
                var c = l[j];
                if (isOpen[c]) continue;
                if (isMine && isMine[c]) { k--; continue; }
                var id = local[c];
                if (id < 0) { id = cells.length; local[c] = id; cells.push(c); }
                (list || (list = [])).push(id);
            }
            if (!list) { if (k !== 0) bad = true; continue; }
            if (k < 0 || k > list.length) bad = true;
            cons.push({ at: i, k: k, list: list });
        }
        // Group the cells by the set of equations they sit in.
        var cellCons = new Array(cells.length), ci;
        for (ci = 0; ci < cells.length; ci++) cellCons[ci] = [];
        for (ci = 0; ci < cons.length; ci++) {
            var cl = cons[ci].list;
            for (var q = 0; q < cl.length; q++) cellCons[cl[q]].push(ci);
        }
        var boxOf = new Int32Array(cells.length), boxes = [], seen = {};
        for (ci = 0; ci < cells.length; ci++) {
            var sig = cellCons[ci].join(','), b = seen[sig];
            if (b === undefined) { b = seen[sig] = boxes.length; boxes.push({ cells: [], cons: cellCons[ci] }); }
            boxes[b].cells.push(cells[ci]);
            boxOf[ci] = b;
        }
        for (ci = 0; ci < cons.length; ci++) {
            var seenBox = {}, bl = [], src = cons[ci].list;
            for (var r = 0; r < src.length; r++) {
                var bx = boxOf[src[r]];
                if (!seenBox[bx]) { seenBox[bx] = 1; bl.push(bx); }
            }
            cons[ci].boxes = bl;
        }
        return { cells: cells, local: local, cons: cons, boxes: boxes, bad: bad };
    }

    // Groups joined by an equation must be counted together; the rest of the
    // board does not care which of their arrangements is the real one — only
    // how many mines it uses.
    function components(sys) {
        var nb = sys.boxes.length, comp = new Int32Array(nb).fill(-1), out = [];
        for (var s = 0; s < nb; s++) {
            if (comp[s] >= 0) continue;
            var id = out.length, order = [], consSeen = {}, consList = [], queue = [s];
            comp[s] = id;
            while (queue.length) {
                var b = queue.shift();
                order.push(b);
                var bc = sys.boxes[b].cons;
                for (var i = 0; i < bc.length; i++) {
                    var c = bc[i];
                    if (consSeen[c]) continue;
                    consSeen[c] = 1;
                    consList.push(c);
                    var cb = sys.cons[c].boxes;
                    for (var j = 0; j < cb.length; j++) {
                        if (comp[cb[j]] < 0) { comp[cb[j]] = id; queue.push(cb[j]); }
                    }
                }
            }
            out.push({ boxes: order, cons: consList });
        }
        return out;
    }

    // Count every arrangement of one component, by the number of mines it uses.
    //   ways[m]            how many arrangements use m mines
    //   mineW[p][m]        … weighted by the share of group p that is mined
    //   canMine / canSafe  whether group p has a mine / a clear cell in SOME arrangement with m mines
    // Returns null when the search is larger than the budget.
    function enumerate(sys, comp, budget, wantWeights) {
        var order = comp.boxes, nBox = order.length, nCon = comp.cons.length, i, j, total = 0;
        var size = new Array(nBox);
        for (i = 0; i < nBox; i++) { size[i] = sys.boxes[order[i]].cells.length; total += size[i]; }
        // The count of mines left on the whole board is NOT applied here: what
        // the digits alone allow is kept apart from what the count adds.
        var maxM = total;
        var need = new Int32Array(nCon), room = new Int32Array(nCon), conPos = {}, boxCons = new Array(nBox);
        for (i = 0; i < nCon; i++) {
            var con = sys.cons[comp.cons[i]];
            conPos[comp.cons[i]] = i;
            need[i] = con.k;
            room[i] = con.list.length;
        }
        for (i = 0; i < nBox; i++) {
            var src = sys.boxes[order[i]].cons, mapped = new Array(src.length);
            for (j = 0; j < src.length; j++) mapped[j] = conPos[src[j]];
            boxCons[i] = mapped;
        }
        var ways = new Float64Array(maxM + 1);
        var mineW = wantWeights ? new Array(nBox) : null;
        var canMine = new Array(nBox), canSafe = new Array(nBox);
        for (i = 0; i < nBox; i++) {
            if (wantWeights) mineW[i] = new Float64Array(maxM + 1);
            canMine[i] = new Uint8Array(maxM + 1);
            canSafe[i] = new Uint8Array(maxM + 1);
        }
        var x = new Int32Array(nBox), nodes = 0, over = false;

        function rec(pos, m, weight) {
            if (over) return;
            if (++nodes > budget) { over = true; return; }
            if (pos === nBox) {
                ways[m] += weight;
                for (var p = 0; p < nBox; p++) {
                    var xv = x[p];
                    if (xv > 0) { canMine[p][m] = 1; if (wantWeights) mineW[p][m] += weight * xv / size[p]; }
                    if (xv < size[p]) canSafe[p][m] = 1;
                }
                return;
            }
            var s = size[pos], bc = boxCons[pos], lo = 0, hi = Math.min(s, maxM - m), k, cc;
            for (k = 0; k < bc.length; k++) {
                cc = bc[k];
                if (need[cc] < hi) hi = need[cc];
                var must = need[cc] - (room[cc] - s);
                if (must > lo) lo = must;
            }
            for (var v = lo; v <= hi; v++) {
                x[pos] = v;
                for (k = 0; k < bc.length; k++) { cc = bc[k]; need[cc] -= v; room[cc] -= s; }
                rec(pos + 1, m + v, weight * SMALL_C[s][v]);
                for (k = 0; k < bc.length; k++) { cc = bc[k]; need[cc] += v; room[cc] += s; }
                if (over) return;
            }
        }
        rec(0, 0, 1);
        counters.nodes += nodes;
        if (over) return null;
        return { size: size, total: total, maxM: maxM, ways: ways, mineW: mineW, canMine: canMine, canSafe: canSafe };
    }

    // Which mine counts of each component can occur in a whole-board
    // arrangement: the counts of all components together must leave between 0
    // and `sea` mines for the cells nobody knows anything about.
    //   sets[i]   Uint8Array over m — component i CAN use m mines (1) or not (0)
    // Returns feasible[i] (same shape) and the feasible totals.
    function feasibleCounts(sets, minesLeft, sea) {
        var n = sets.length, size = minesLeft + 1, i, a, b;
        var prefix = new Array(n + 1), suffix = new Array(n + 1);
        prefix[0] = new Uint8Array(size); prefix[0][0] = 1;
        suffix[n] = new Uint8Array(size); suffix[n][0] = 1;
        function conv(p, s) {
            var out = new Uint8Array(size);
            for (a = 0; a < size; a++) {
                if (!p[a]) continue;
                for (b = 0; b < s.length && a + b < size; b++) if (s[b]) out[a + b] = 1;
            }
            return out;
        }
        for (i = 0; i < n; i++) prefix[i + 1] = conv(prefix[i], sets[i]);
        for (i = n - 1; i >= 0; i--) suffix[i] = conv(suffix[i + 1], sets[i]);
        var lowest = Math.max(0, minesLeft - sea), totals = new Uint8Array(size), any = false;
        for (a = lowest; a < size; a++) if (prefix[n][a]) { totals[a] = 1; any = true; }
        var feasible = new Array(n);
        for (i = 0; i < n; i++) {
            var others = conv(prefix[i], suffix[i + 1]), f = new Uint8Array(sets[i].length);
            for (a = 0; a < sets[i].length; a++) {
                if (!sets[i][a]) continue;
                for (b = Math.max(0, lowest - a); a + b < size; b++) if (others[b]) { f[a] = 1; break; }
            }
            feasible[i] = f;
        }
        return { feasible: feasible, totals: totals, any: any, lowest: lowest };
    }

    // ── Exact analysis of a position ─────────────────────────────────────────
    //   view  {w, h, mines, open: Uint8Array, num: Uint8Array}
    //   opts  {knownMines: Uint8Array, probabilities: true, budget, limitTo}
    // Returns
    //   status   'ok' | 'partial' (a region was too large to count; what is reported is still certain)
    //            | 'contradiction' (the position cannot be a real game)
    //   safe, mines        cells proven by the digits alone
    //   safeByCount, minesByCount   cells proven only together with the number of mines left
    //   prob     Float64Array, −1 for open cells; null when probabilities were not asked for
    function analyze(view, opts) {
        opts = opts || {};
        var t = Core.topology(view.w, view.h), n = t.n;
        var isOpen = view.open, num = view.num, known = opts.knownMines || null;
        var wantProb = opts.probabilities !== false;
        var budget = opts.budget || BUDGET_ANALYZE;
        var sys = buildSystem(t, isOpen, num, known, opts.limitTo || null);
        var res = { status: 'ok', safe: [], mines: [], safeByCount: [], minesByCount: [], prob: null, sea: 0, frontier: sys.cells.length, system: sys };
        counters.analyses++;
        if (sys.bad) { res.status = 'contradiction'; return res; }

        var knownCount = 0, sea = 0, i;
        if (known) for (i = 0; i < n; i++) if (known[i]) knownCount++;
        for (i = 0; i < n; i++) if (!isOpen[i] && !(known && known[i]) && sys.local[i] < 0) sea++;
        var minesLeft = view.mines - knownCount;
        res.sea = sea;
        res.minesLeft = minesLeft;
        if (minesLeft < 0) { res.status = 'contradiction'; return res; }

        var comps = components(sys), sets = [], en = [], c, p, m;
        for (c = 0; c < comps.length; c++) {
            var e = enumerate(sys, comps[c], budget, wantProb);
            en.push(e);
            if (e) {
                var set = new Uint8Array(e.maxM + 1);
                for (m = 0; m <= e.maxM; m++) if (e.ways[m] > 0) set[m] = 1;
                sets.push(set);
            } else {
                // Too large to count: any number of mines it could hold is
                // assumed possible — fewer certainties elsewhere, never a wrong one.
                res.status = 'partial';
                counters.partial++;
                var tot = 0;
                for (p = 0; p < comps[c].boxes.length; p++) tot += sys.boxes[comps[c].boxes[p]].cells.length;
                sets.push(new Uint8Array(Math.min(tot, minesLeft) + 1).fill(1));
            }
        }
        var fc = feasibleCounts(sets, minesLeft, sea);
        if (!fc.any) { res.status = 'contradiction'; return res; }

        // Certainties.
        for (c = 0; c < comps.length; c++) {
            var ee = en[c];
            if (!ee) continue;
            var feas = fc.feasible[c];
            for (p = 0; p < comps[c].boxes.length; p++) {
                var mineAll = false, safeAll = false, mineLocal = false, safeLocal = false;
                for (m = 0; m <= ee.maxM; m++) {
                    if (!(ee.ways[m] > 0)) continue;
                    if (ee.canMine[p][m]) mineLocal = true;
                    if (ee.canSafe[p][m]) safeLocal = true;
                    if (!feas[m]) continue;
                    if (ee.canMine[p][m]) mineAll = true;
                    if (ee.canSafe[p][m]) safeAll = true;
                }
                var cellsOfBox = sys.boxes[comps[c].boxes[p]].cells, target = null;
                if (!mineAll) target = mineLocal ? res.safeByCount : res.safe;
                else if (!safeAll) target = safeLocal ? res.minesByCount : res.mines;
                if (target) for (i = 0; i < cellsOfBox.length; i++) target.push(cellsOfBox[i]);
            }
        }
        // The cells no digit touches: certain only through the count of mines left.
        var loT = -1, hiT = -1;
        for (m = 0; m <= minesLeft; m++) if (fc.totals[m]) { if (loT < 0) loT = m; hiT = m; }
        var seaSafe = sea > 0 && loT === minesLeft, seaMines = sea > 0 && hiT === minesLeft - sea;
        if (seaSafe || seaMines) {
            var into = seaSafe ? res.safeByCount : res.minesByCount;
            for (i = 0; i < n; i++) if (!isOpen[i] && !(known && known[i]) && sys.local[i] < 0) into.push(i);
        }

        if (wantProb) res.prob = probabilities(view, sys, comps, en, fc, minesLeft, sea, known);
        res.comps = comps;
        return res;
    }

    // Probability of a mine in every unknown cell: each whole-board arrangement
    // is equally likely, so a component's arrangement with m mines weighs as
    // many times as the rest of the board can be completed around it.
    function probabilities(view, sys, comps, en, fc, minesLeft, sea, known) {
        var t = Core.topology(view.w, view.h), n = t.n, prob = new Float64Array(n).fill(-1);
        var nc = comps.length, c, m, i, a, b, size = minesLeft + 1;
        // Each component's count polynomial, scaled to its own maximum (the
        // scale cancels in every ratio below).
        var poly = new Array(nc);
        for (c = 0; c < nc; c++) {
            var e = en[c], pl = new Float64Array(Math.min(size, (e ? e.maxM : 0) + 1)), top = 0;
            if (e) {
                for (m = 0; m < pl.length; m++) if (fc.feasible[c][m] && e.ways[m] > top) top = e.ways[m];
                for (m = 0; m < pl.length; m++) pl[m] = (fc.feasible[c][m] && top > 0) ? e.ways[m] / top : 0;
                poly[c] = { p: pl, scale: top };
            } else {
                poly[c] = null;
            }
        }
        // A region too large to count leaves the odds unknown everywhere.
        for (c = 0; c < nc; c++) if (!poly[c]) return null;

        function conv(x, y) {
            var out = new Float64Array(size);
            for (a = 0; a < x.length; a++) {
                if (!x[a]) continue;
                for (b = 0; b < y.length && a + b < size; b++) if (y[b]) out[a + b] += x[a] * y[b];
            }
            return out;
        }
        var one = new Float64Array(1); one[0] = 1;
        var prefix = new Array(nc + 1), suffix = new Array(nc + 1);
        prefix[0] = one; suffix[nc] = one;
        for (c = 0; c < nc; c++) prefix[c + 1] = normalise(conv(prefix[c], poly[c].p));
        for (c = nc - 1; c >= 0; c--) suffix[c] = normalise(conv(suffix[c + 1], poly[c].p));
        function normalise(arr) {
            var mx = 0;
            for (var q = 0; q < arr.length; q++) if (arr[q] > mx) mx = arr[q];
            if (mx > 0 && (mx > 1e200 || mx < 1e-200)) for (q = 0; q < arr.length; q++) arr[q] /= mx;
            return arr;
        }
        // Ways to put j mines into the sea, as a ratio to the largest such number in range.
        var seaW = new Float64Array(size), topLog = -Infinity, lg = new Float64Array(size);
        for (i = 0; i < size; i++) { lg[i] = logChoose(sea, i); if (lg[i] > topLog && fc.totals[minesLeft - i]) topLog = lg[i]; }
        if (topLog === -Infinity) for (i = 0; i < size; i++) if (lg[i] > topLog) topLog = lg[i];
        for (i = 0; i < size; i++) seaW[i] = lg[i] === -Infinity ? 0 : Math.exp(lg[i] - topLog);

        // Frontier cells.
        for (c = 0; c < nc; c++) {
            var others = conv(prefix[c], suffix[c + 1]), ee = en[c], pc = poly[c];
            // rest[m] — weight of everything outside this component when it uses m mines
            var rest = new Float64Array(pc.p.length), z = 0;
            for (m = 0; m < pc.p.length; m++) {
                if (!pc.p[m]) continue;
                var acc = 0;
                for (b = 0; b + m <= minesLeft; b++) if (others[b]) acc += others[b] * seaW[minesLeft - m - b];
                rest[m] = acc;
                z += pc.p[m] * acc;
            }
            for (var p = 0; p < comps[c].boxes.length; p++) {
                var w = 0;
                for (m = 0; m < pc.p.length; m++) if (rest[m] && fc.feasible[c][m]) w += (ee.mineW[p][m] / pc.scale) * rest[m];
                var val = z > 0 ? w / z : 0;
                if (val < 0) val = 0; else if (val > 1) val = 1;
                var cs = sys.boxes[comps[c].boxes[p]].cells;
                for (i = 0; i < cs.length; i++) prob[cs[i]] = val;
            }
        }
        // The sea: the expected number of mines left for it, shared equally.
        if (sea > 0) {
            var all = prefix[nc], zt = 0, ex = 0;
            for (m = 0; m < all.length && m <= minesLeft; m++) {
                if (!all[m]) continue;
                var wt = all[m] * seaW[minesLeft - m];
                zt += wt;
                ex += wt * (minesLeft - m);
            }
            var ps = zt > 0 ? ex / (zt * sea) : 0;
            if (ps < 0) ps = 0; else if (ps > 1) ps = 1;
            for (i = 0; i < n; i++) if (!view.open[i] && !(known && known[i]) && sys.local[i] < 0) prob[i] = ps;
        }
        if (known) for (i = 0; i < n; i++) if (known[i]) prob[i] = 1;
        return prob;
    }

    // ── A whole board by logic alone ─────────────────────────────────────────
    // Plays from one opening the way a perfect logician would: single digits
    // first, then pairs of digits, then exact counting of whole regions with
    // the number of mines left. `solved: false` means a guess was unavoidable
    // (or, with `stuck`, where exactly the logic ran out).
    //   opts {budget, maxTier: 1|2|3, state: true}
    function solve(w, h, mine, start, opts) {
        opts = opts || {};
        var t = Core.topology(w, h), n = t.n, nb = t.nb;
        var num = opts.num || Core.numbers(w, h, mine);
        var total = Core.countMines(mine), safeTotal = n - total;
        var st = new Uint8Array(n);                 // 0 unknown · 1 open · 2 known mine
        var unk = new Uint8Array(n), mk = new Uint8Array(n);
        var inQ = new Uint8Array(n), queue = [], opened = 0, foundMines = 0, i;
        var maxTier = opts.maxTier || 3, usedTier = 1, usedCount = false;
        var error = null;
        for (i = 0; i < n; i++) unk[i] = nb[i].length;

        function push(c) { if (!inQ[c] && st[c] === 1 && num[c] > 0) { inQ[c] = 1; queue.push(c); } }
        function reveal(c0) {
            if (st[c0] !== 0) return;
            if (mine[c0]) { error = 'opened a mine at ' + c0; return; }
            var stack = [c0];
            st[c0] = 1; opened++;
            while (stack.length) {
                var z = stack.pop(), l = nb[z], k;
                for (k = 0; k < l.length; k++) { unk[l[k]]--; push(l[k]); }
                push(z);
                if (num[z] !== 0) continue;
                for (k = 0; k < l.length; k++) {
                    var c = l[k];
                    if (st[c] !== 0) continue;
                    if (mine[c]) { error = 'opening next to a blank hit a mine at ' + c; return; }
                    st[c] = 1; opened++;
                    stack.push(c);
                }
            }
        }
        function markMine(c) {
            if (st[c] !== 0) return;
            if (!mine[c]) { error = 'marked a clear cell as a mine at ' + c; return; }
            st[c] = 2; foundMines++;
            var l = nb[c];
            for (var k = 0; k < l.length; k++) { unk[l[k]]--; mk[l[k]]++; push(l[k]); }
        }
        function tier1() {
            while (queue.length && !error) {
                var c = queue.pop();
                inQ[c] = 0;
                if (unk[c] === 0) continue;
                var need = num[c] - mk[c], l = nb[c], k;
                if (need === 0) { for (k = 0; k < l.length; k++) if (st[l[k]] === 0) reveal(l[k]); }
                else if (need === unk[c]) { for (k = 0; k < l.length; k++) if (st[l[k]] === 0) markMine(l[k]); }
            }
        }
        // Two digits at once: what one says about the cells they share bounds
        // what the other can have in the cells that are only its own.
        var stamp = new Int32Array(n), stampId = 0;
        function tier2() {
            var progress = false;
            for (var a = 0; a < n && !error; a++) {
                if (st[a] !== 1 || unk[a] === 0 || num[a] === 0) continue;
                var ax = a % w, ay = (a - ax) / w;
                for (var dy = -2; dy <= 2; dy++) {
                    var by = ay + dy;
                    if (by < 0 || by >= h) continue;
                    for (var dx = -2; dx <= 2; dx++) {
                        var bx = ax + dx;
                        if (bx < 0 || bx >= w || (!dx && !dy)) continue;
                        var b = by * w + bx;
                        if (st[b] !== 1 || unk[b] === 0 || num[b] === 0) continue;
                        if (unk[a] === 0) break;
                        var la = nb[a], lb = nb[b], k, common = 0, onlyA = 0;
                        stampId++;
                        for (k = 0; k < lb.length; k++) if (st[lb[k]] === 0) stamp[lb[k]] = stampId;
                        for (k = 0; k < la.length; k++) {
                            if (st[la[k]] !== 0) continue;
                            if (stamp[la[k]] === stampId) common++; else onlyA++;
                        }
                        if (!common || !onlyA) continue;
                        var ka = num[a] - mk[a], kb = num[b] - mk[b], onlyB = unk[b] - common;
                        var minCommon = Math.max(0, ka - onlyA, kb - onlyB), maxCommon = Math.min(common, ka, kb);
                        if (ka - minCommon === 0) {
                            for (k = 0; k < la.length; k++) if (st[la[k]] === 0 && stamp[la[k]] !== stampId) reveal(la[k]);
                            progress = true;
                        } else if (ka - maxCommon === onlyA) {
                            for (k = 0; k < la.length; k++) if (st[la[k]] === 0 && stamp[la[k]] !== stampId) markMine(la[k]);
                            progress = true;
                        }
                    }
                }
            }
            return progress;
        }
        function tier3() {
            var isOpen = new Uint8Array(n), known = new Uint8Array(n);
            for (var c = 0; c < n; c++) { if (st[c] === 1) isOpen[c] = 1; else if (st[c] === 2) known[c] = 1; }
            var a = analyze({ w: w, h: h, mines: total, open: isOpen, num: num }, { knownMines: known, probabilities: false, budget: opts.budget || BUDGET_SOLVE });
            if (a.status === 'contradiction') { error = 'position became impossible'; return false; }
            var k, any = false;
            if (a.safe.length || a.mines.length) {
                for (k = 0; k < a.mines.length; k++) markMine(a.mines[k]);
                for (k = 0; k < a.safe.length; k++) reveal(a.safe[k]);
                any = true;
            }
            if (a.safeByCount.length || a.minesByCount.length) {
                usedCount = true;
                for (k = 0; k < a.minesByCount.length; k++) markMine(a.minesByCount[k]);
                for (k = 0; k < a.safeByCount.length; k++) reveal(a.safeByCount[k]);
                any = true;
            }
            return any;
        }

        reveal(start);
        while (!error) {
            tier1();
            if (error || opened === safeTotal) break;
            if (maxTier >= 2 && tier2()) { if (usedTier < 2) usedTier = 2; continue; }
            if (maxTier >= 3 && tier3()) { usedTier = 3; continue; }
            break;
        }
        var out = { solved: !error && opened === safeTotal, opened: opened, safeTotal: safeTotal, tier: usedTier, usedCount: usedCount, error: error };
        if (opts.state || !out.solved) out.st = st;
        return out;
    }

    // ── The next deduction, with its proof ───────────────────────────────────
    //   view  {w, h, mines, open, num, flag}
    //   opts  {near: cell} — prefer a deduction close to where the player is looking
    // Returns
    //   {kind: 'safe' | 'mine', cells, proof: {rule: 'single' | 'pair' | 'region' | 'count', numbers: [cells], flags: [cells]}}
    //   {kind: 'wrongflag', cells, proof}   a flag stands on a provably clear cell
    //   {kind: 'guess', cells: [safest], risk, prob}   nothing can be proven anywhere
    //   {kind: 'done'}
    function hint(view, opts) {
        opts = opts || {};
        var t = Core.topology(view.w, view.h), n = t.n, nb = t.nb, i, k;
        var full = analyze(view, { probabilities: true, budget: opts.budget });
        if (full.status === 'contradiction') return { kind: 'none', reason: 'contradiction' };
        var isSafe = new Uint8Array(n), isMine = new Uint8Array(n);
        mark(full.safe, isSafe); mark(full.safeByCount, isSafe);
        mark(full.mines, isMine); mark(full.minesByCount, isMine);
        function mark(list, arr) { for (var q = 0; q < list.length; q++) arr[list[q]] = 1; }

        var flag = view.flag || new Uint8Array(n), told = opts.trust || null;
        // A flag is trusted only if the digits prove it; a flag that is merely a
        // guess proves nothing, and a flag on a provably clear cell is the hint.
        // `trust` — mines an earlier hint has already shown: the next hint
        // builds on them even if the player did not put the flag down.
        var trusted = new Uint8Array(n), wrong = [];
        for (i = 0; i < n; i++) {
            if (view.open[i]) continue;
            if (told && told[i] && isMine[i]) { trusted[i] = 1; continue; }
            if (!flag[i]) continue;
            if (isMine[i]) trusted[i] = 1;
            else if (isSafe[i]) wrong.push(i);
        }
        var near = opts.near;
        function dist(c) {
            if (near == null || near < 0) return 0;
            return Math.max(Math.abs(c % view.w - near % view.w), Math.abs(Math.floor(c / view.w) - Math.floor(near / view.w)));
        }
        function nearest(list) {
            var best = list[0], bd = dist(best);
            for (var q = 1; q < list.length; q++) { var d = dist(list[q]); if (d < bd) { bd = d; best = list[q]; } }
            return best;
        }
        function unknownAround(c, tr) {
            var l = nb[c], out = [];
            for (var q = 0; q < l.length; q++) if (!view.open[l[q]] && !tr[l[q]]) out.push(l[q]);
            return out;
        }
        function trustedAround(c, tr) {
            var l = nb[c], out = [];
            for (var q = 0; q < l.length; q++) if (!view.open[l[q]] && tr[l[q]]) out.push(l[q]);
            return out;
        }
        // One digit: it already has all its mines, or it needs every cell it has left.
        function single(tr, want) {
            var best = null;
            for (var c = 0; c < n; c++) {
                if (!view.open[c] || view.num[c] === 0) continue;
                var u = unknownAround(c, tr);
                if (!u.length) continue;
                var fl = trustedAround(c, tr), need = view.num[c] - fl.length;
                var kind = need === 0 ? 'safe' : (need === u.length ? 'mine' : null);
                if (kind !== want) continue;
                var cells = u;
                var cand = { kind: kind, cells: cells, proof: { rule: 'single', numbers: [c], flags: fl, need: need } };
                if (!best || dist(nearest(cells)) < dist(nearest(best.cells))) best = cand;
            }
            return best;
        }
        // Two digits: the cells they share bound what is left for the cells of one alone.
        function pair(tr, want) {
            var best = null, w = view.w, h = view.h;
            for (var a = 0; a < n; a++) {
                if (!view.open[a] || view.num[a] === 0) continue;
                var ua = unknownAround(a, tr);
                if (!ua.length) continue;
                var ax = a % w, ay = (a - ax) / w, ka = view.num[a] - trustedAround(a, tr).length;
                for (var dy = -2; dy <= 2; dy++) {
                    for (var dx = -2; dx <= 2; dx++) {
                        var bx = ax + dx, by = ay + dy;
                        if ((!dx && !dy) || bx < 0 || bx >= w || by < 0 || by >= h) continue;
                        var b = by * w + bx;
                        if (!view.open[b] || view.num[b] === 0) continue;
                        var ub = unknownAround(b, tr);
                        if (!ub.length) continue;
                        var inB = {}, q;
                        for (q = 0; q < ub.length; q++) inB[ub[q]] = 1;
                        var common = [], onlyA = [];
                        for (q = 0; q < ua.length; q++) (inB[ua[q]] ? common : onlyA).push(ua[q]);
                        if (!common.length || !onlyA.length) continue;
                        var kb = view.num[b] - trustedAround(b, tr).length, onlyB = ub.length - common.length;
                        var minCommon = Math.max(0, ka - onlyA.length, kb - onlyB), maxCommon = Math.min(common.length, ka, kb);
                        var kind = (ka - minCommon === 0) ? 'safe' : ((ka - maxCommon === onlyA.length) ? 'mine' : null);
                        if (kind !== want) continue;
                        var cells = onlyA;
                        var fl = trustedAround(a, tr).concat(trustedAround(b, tr));
                        var cand = { kind: kind, cells: cells, proof: { rule: 'pair', numbers: [a, b], flags: fl, shared: common } };
                        if (!best || dist(nearest(cells)) < dist(nearest(best.cells))) best = cand;
                    }
                }
            }
            return best;
        }
        // A whole region: the smallest ring of digits around the cell that still proves it.
        function region(cell, wantMine) {
            var limit = new Uint8Array(n), ring = [], q, r;
            var l = nb[cell];
            for (q = 0; q < l.length; q++) if (view.open[l[q]]) { limit[l[q]] = 1; ring.push(l[q]); }
            for (var grow = 0; grow < 6; grow++) {
                var a = analyze(view, { knownMines: trusted, probabilities: false, limitTo: limit });
                var list = wantMine ? a.mines : a.safe;
                if (a.status !== 'contradiction' && list.indexOf(cell) >= 0) {
                    var numbers = [], flags = [];
                    for (q = 0; q < n; q++) if (limit[q] && view.open[q] && view.num[q] > 0) {
                        var has = false, ln = nb[q];
                        for (r = 0; r < ln.length; r++) if (!view.open[ln[r]]) { has = true; if (trusted[ln[r]] && flags.indexOf(ln[r]) < 0) flags.push(ln[r]); }
                        if (has) numbers.push(q);
                    }
                    return { rule: 'region', numbers: numbers, flags: flags };
                }
                // Widen: every digit that shares an unknown cell with the ring.
                var add = [];
                for (q = 0; q < ring.length; q++) {
                    var lr = nb[ring[q]];
                    for (r = 0; r < lr.length; r++) {
                        if (view.open[lr[r]]) continue;
                        var l2 = nb[lr[r]];
                        for (var s = 0; s < l2.length; s++) if (view.open[l2[s]] && !limit[l2[s]]) { limit[l2[s]] = 1; add.push(l2[s]); }
                    }
                }
                if (!add.length) break;
                ring = ring.concat(add);
            }
            return null;
        }

        if (wrong.length) {
            var wc = nearest(wrong), wp = region(wc, false);
            return { kind: 'wrongflag', cells: [wc], proof: wp || { rule: 'count', numbers: [], flags: [] } };
        }

        var anySafe = false;
        for (i = 0; i < n; i++) if (isSafe[i]) { anySafe = true; break; }
        if (anySafe) {
            var found = single(trusted, 'safe') || pair(trusted, 'safe');
            if (found) return found;
            // A mine that, once flagged, turns a digit into an obvious "all the rest are clear".
            var m1 = single(trusted, 'mine') || pair(trusted, 'mine');
            if (m1) return m1;
            // Nothing simple: a region proof for a clear cell, the nearest first.
            var safeCells = [];
            for (i = 0; i < n; i++) if (isSafe[i]) safeCells.push(i);
            safeCells.sort(function (x, y) { return dist(x) - dist(y); });
            for (k = 0; k < safeCells.length && k < 12; k++) {
                var pr = region(safeCells[k], false);
                if (pr) return { kind: 'safe', cells: [safeCells[k]], proof: pr };
            }
            // Only the count of mines left proves it.
            var byCount = full.safeByCount.length ? nearest(full.safeByCount) : safeCells[0];
            return { kind: 'safe', cells: [byCount], proof: { rule: 'count', numbers: [], flags: [], minesLeft: full.minesLeft } };
        }

        var left = 0;
        for (i = 0; i < n; i++) if (!view.open[i]) left++;
        if (left === view.mines) return { kind: 'done' };

        // No clear cell can be proven anywhere: an honest guess, with the odds.
        if (!full.prob) return { kind: 'none', reason: 'too-large' };
        var bestP = 2, bestCells = [];
        for (i = 0; i < n; i++) {
            if (view.open[i] || flag[i] && isMine[i]) continue;
            var pv = full.prob[i];
            if (pv < 0) continue;
            if (pv < bestP - 1e-9) { bestP = pv; bestCells = [i]; }
            else if (Math.abs(pv - bestP) <= 1e-9) bestCells.push(i);
        }
        if (!bestCells.length) return { kind: 'done' };
        return { kind: 'guess', cells: [nearest(bestCells)], ties: bestCells.length, risk: bestP, prob: full.prob };
    }

    // ── Why the game was lost ────────────────────────────────────────────────
    //   view   the position BEFORE the fatal move (flags ignored)
    //   cell   the cell that was opened
    // Returns {verdict, …}
    //   'certain-mine'   the digits proved this cell was a mine          → proof
    //   'safe-existed'   a provably clear cell was available elsewhere   → safe: [cells], proof for the first
    //   'forced-guess'   nothing was provable anywhere                   → risk, bestRisk, best: [cells], even
    function explainLoss(view, cell, opts) {
        opts = opts || {};
        var bare = { w: view.w, h: view.h, mines: view.mines, open: view.open, num: view.num };
        var a = analyze(bare, { probabilities: true, budget: opts.budget });
        if (a.status === 'contradiction') return { verdict: 'unknown' };
        var safe = a.safe.concat(a.safeByCount), mines = a.mines.concat(a.minesByCount);
        var risk = a.prob ? a.prob[cell] : null, out;
        if (mines.indexOf(cell) >= 0) {
            out = { verdict: 'certain-mine', risk: 1, safe: safe };
            var h1 = hintFor(bare, cell, true);
            if (h1) out.proof = h1;
            return out;
        }
        if (safe.length) {
            out = { verdict: 'safe-existed', risk: risk, safe: safe };
            // The simplest proof of one of them; a mine that has to be seen
            // first is taken as seen and shown as part of the proof.
            var trust = new Uint8Array(view.open.length), hs = null;
            for (var step = 0; step < 12; step++) {
                hs = hint(bare, { near: cell, trust: trust, budget: opts.budget });
                if (!hs || hs.kind !== 'mine') break;
                for (var q = 0; q < hs.cells.length; q++) trust[hs.cells[q]] = 1;
            }
            if (hs && hs.kind === 'safe') { out.example = hs.cells; out.proof = hs.proof; }
            return out;
        }
        if (!a.prob) return { verdict: 'unknown' };
        var best = 2, bestCells = [];
        for (var i = 0; i < a.prob.length; i++) {
            var p = a.prob[i];
            if (p < 0) continue;
            if (p < best - 1e-9) { best = p; bestCells = [i]; }
            else if (Math.abs(p - best) <= 1e-9) bestCells.push(i);
        }
        return {
            verdict: 'forced-guess', risk: risk, bestRisk: best, best: bestCells,
            even: risk !== null && Math.abs(risk - 0.5) < 1e-9,
            pickedBest: risk !== null && risk <= best + 1e-9,
            prob: a.prob
        };
    }
    // The proof for one given cell (used when the cell is already known to be provable).
    function hintFor(view, cell, wantMine) {
        var t = Core.topology(view.w, view.h), n = t.n, nb = t.nb, limit = new Uint8Array(n), ring = [], q, r;
        var l = nb[cell];
        for (q = 0; q < l.length; q++) if (view.open[l[q]]) { limit[l[q]] = 1; ring.push(l[q]); }
        for (var grow = 0; grow < 6; grow++) {
            var a = analyze(view, { probabilities: false, limitTo: limit });
            var list = wantMine ? a.mines : a.safe;
            if (a.status !== 'contradiction' && list.indexOf(cell) >= 0) {
                var numbers = [];
                for (q = 0; q < n; q++) if (limit[q] && view.open[q] && view.num[q] > 0) {
                    var ln = nb[q];
                    for (r = 0; r < ln.length; r++) if (!view.open[ln[r]]) { numbers.push(q); break; }
                }
                return { rule: numbers.length === 1 ? 'single' : (numbers.length === 2 ? 'pair' : 'region'), numbers: numbers, flags: [] };
            }
            var add = [];
            for (q = 0; q < ring.length; q++) {
                var lr = nb[ring[q]];
                for (r = 0; r < lr.length; r++) {
                    if (view.open[lr[r]]) continue;
                    var l2 = nb[lr[r]];
                    for (var s = 0; s < l2.length; s++) if (view.open[l2[s]] && !limit[l2[s]]) { limit[l2[s]] = 1; add.push(l2[s]); }
                }
            }
            if (!add.length) break;
            ring = ring.concat(add);
        }
        return { rule: 'count', numbers: [], flags: [] };
    }

    root.MinesSolver = {
        analyze: analyze,
        solve: solve,
        hint: hint,
        explainLoss: explainLoss,
        counters: counters,
        BUDGET_ANALYZE: BUDGET_ANALYZE,
        BUDGET_SOLVE: BUDGET_SOLVE
    };
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this));
